import { useCallback, useEffect, useMemo, useState } from 'react';

import 'react-tooltip/dist/react-tooltip.css';
// @note remove this when bootstrap is not needed
import 'bootstrap/dist/css/bootstrap.min.css';

import { AUTHENTICATION_PATHNAME } from 'middleware';
import type { AppProps, AppContext } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { CookiesProvider } from 'react-cookie';
import Modal from 'react-modal';
import { RelayEnvironmentProvider, fetchQuery } from 'react-relay/hooks';
import { ToastProvider } from 'react-toast-notifications';

import { ErrorBoundary } from 'components/ErrorBoundary';
import EppoFeatureFlagProvider from 'components/feature-flag/EppoFeatureFlagProvider';
import RandomTargetingIndicator from 'components/feature-flag/RandomTargetingIndicator';
import { IntercomProvider } from 'components/intercom/IntercomProvider';
import { ErrorFallback } from 'components/layout/ErrorFallback';
import MenuOptionsSidebar from 'components/layout/MenuOptionsSidebar';
import { SidebarContainer } from 'components/layout/nav/styled';
import { AuthenticatedPageLayoutContainer } from 'components/layout/styled';
import { PageLoader } from 'components/loaders/PageLoader';
import LoginPage from 'components/login/LoginPage';
import { RbacPermissionsProvider } from 'components/RbacPermissionsProvider';
import { loadMazeSnippet } from 'components/scripts/load-maze';
import { StoredDataProvider } from 'components/StoredDataProvider';
import { ZendeskProvider } from 'components/zendesk/ZendeskProvider';

import { authorizeUserQuery } from 'lib/__generated__/authorizeUserQuery.graphql';
import { AUTH_QUERY } from 'lib/authorize-user-query';
import makeEnvironment from 'lib/relay';
import { GlobalContext, useValue } from 'lib/state';
import { IUserContext, UserContext, UserInfo } from 'lib/user-context';

import { initDatadogRum } from 'helpers/datadog-helper';
import initializeThirdPartyTools from 'helpers/initializeThirdPartyTools';
import { page } from 'helpers/rudderstack-helper';

import { CustomToastContainer } from 'hooks/useCustomToastContainer';
import { useNavCollapsed } from 'hooks/useNavCollapsed';

import '../styles/fonts.css';

import GlobalStyle from 'styles/GlobalStyle';

Modal.setAppElement('#__next');

initDatadogRum();

function App({ Component, pageProps, router }: AppProps) {
  const [state, dispatch] = useValue();
  const relayEnvironment = makeEnvironment();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore maintain behavior by using an empty userInfo before logged in/loaded
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const pathname = router.pathname;
  const isLoginPage = pathname === AUTHENTICATION_PATHNAME;
  const [isLoading, setIsLoading] = useState(!isLoginPage);
  const isWelcomePage = pathname === '/welcome';

  const reloadUserContext = useCallback(async () => {
    try {
      const res = await fetchQuery<authorizeUserQuery>(
        relayEnvironment,
        AUTH_QUERY,
        {},
      ).toPromise();
      const respUser = res?.userContext ?? null;

      if (respUser?.userId) {
        setUserInfo(respUser);
      }
    } catch (error) {
      console.error('Auth: Error fetching userContext.', { error });
    }
  }, [relayEnvironment]);

  const userContext = useMemo<IUserContext>(() => ({ ...userInfo, reloadUserContext }), [
    userInfo,
    reloadUserContext,
  ]);

  useEffect(() => {
    if (isLoginPage) return;

    async function updateUser() {
      await reloadUserContext();
      setIsLoading(false);
    }
    updateUser().catch(console.error);
  }, [reloadUserContext, isLoginPage]);

  useEffect(() => {
    function handleRouteChange() {
      page();
    }
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => router.events.off('routeChangeComplete', handleRouteChange);
  });

  // Generate a randomized targeting key if URL parameter is present
  const randomizedTargetingKey = useMemo(() => {
    let randomKey = null;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.get('useRandomSubject') === 'true') {
        // Generate a random ID for targetingKey
        randomKey = `random-${Math.random().toString(36).substring(2, 15)}`;
      }

      if (urlParams.get('throwError') === 'true') {
        // Throw an error after the page has loaded
        setTimeout(() => {
          throw new Error('Thrown delayed error from URL parameter');
        }, 1000);
      }
    }
    return randomKey;
  }, []);

  useEffect(() => {
    if (userInfo?.userId) {
      initializeThirdPartyTools(userInfo.userId, relayEnvironment, randomizedTargetingKey).catch(
        console.error,
      );
    }
  }, [userInfo, relayEnvironment, randomizedTargetingKey]);

  const { NavCollapsedContext, collapsed, toggleCollapsed } = useNavCollapsed();

  useEffect(() => {
    const appId = process.env.NEXT_PUBLIC_MAZE_APP_ID;
    if (appId) {
      loadMazeSnippet(appId);
    }
  }, []);

  let pageBody;
  if (isLoading) {
    pageBody = <PageLoader />;
  } else if (!userInfo?.userId) {
    pageBody = <LoginPage />;
  } else {
    pageBody = (
      <UserContext.Provider value={userContext}>
        <EppoFeatureFlagProvider
          waitForInitialization={false}
          randomizedTargetingKey={randomizedTargetingKey}
        >
          <RbacPermissionsProvider>
            <IntercomProvider>
              <ZendeskProvider>
                <ToastProvider
                  placement="bottom-center"
                  components={{ ToastContainer: CustomToastContainer }}
                >
                  {router.isReady && isWelcomePage ? (
                    <Component {...pageProps} />
                  ) : (
                    <AuthenticatedPageLayoutContainer data-cy-element-type="auth-page-layout">
                      <NavCollapsedContext.Provider value={{ collapsed, toggleCollapsed }}>
                        <SidebarContainer collapsed={collapsed}>
                          <MenuOptionsSidebar />
                        </SidebarContainer>
                        <Component {...pageProps} />
                      </NavCollapsedContext.Provider>
                    </AuthenticatedPageLayoutContainer>
                  )}
                </ToastProvider>
              </ZendeskProvider>
            </IntercomProvider>
          </RbacPermissionsProvider>
        </EppoFeatureFlagProvider>
      </UserContext.Provider>
    );
  }

  return (
    // Top-level safety net: render the Oops! page for any error that escapes a
    // nearer boundary (e.g. a page that throws during render, like an
    // unauthorized admin route). Without a fallback this rendered null, which
    // surfaced as a blank white screen in production builds (React unmounts the
    // tree on an uncaught error in prod; in dev the error overlay hid it).
    <ErrorBoundary fallback={<ErrorFallback />}>
      <RelayEnvironmentProvider environment={relayEnvironment}>
        <StoredDataProvider isAuthenticated={!!userInfo?.userId}>
          <GlobalContext.Provider value={[state, dispatch]}>
            <CookiesProvider>
              <Head>
                <meta name="viewport" content="initial-scale=1.0, width=device-width" />
                <link rel="shortcut icon" href="/favicon.svg" />
              </Head>
              <GlobalStyle />
              <RandomTargetingIndicator randomizedTargetingKey={randomizedTargetingKey} />
              {pageBody}
            </CookiesProvider>
          </GlobalContext.Provider>
        </StoredDataProvider>
      </RelayEnvironmentProvider>
    </ErrorBoundary>
  );
}

// Disable Next.js Automatic Static Optimization. All pages require authentication
// so static pre-rendering is not useful, and it causes build errors where _document's
// HTML component leaks into page chunks during static generation in Next.js 15.
App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext);
  return { ...appProps };
};

export default App;
